/**
 * journeyResolver.js
 *
 * One confidence-scored answer to "what is this (anonymous or logged-in)
 * visitor actually doing?" It blends three client-side signals into a
 * single resolved { project, stage, confidence }:
 *
 *   1. Declared stage      - what they tapped (localStorage journey-stage)
 *   2. Inferred stage      - argmax of behavioural stage signals we tally
 *                            as they browse (a choosing page -> choose, a
 *                            cost page -> plan, etc.)
 *   3. Project focus       - the project they engage with most, weighted
 *                            by frequency + recency (not just "last seen")
 *
 * Everything is stored in the browser (no backend read needed), so it
 * works for anonymous visitors and needs no account.
 */

import {
  IMPROVEMENT_PROJECTS,
  isImprovementStage,
  JOURNEY_STORAGE_KEY,
  JOURNEY_PROJECT_KEY,
  JOURNEY_DECLARED_KEY,
} from './improvementJourney';

const SIGNALS_KEY = 'bp:journey-signals';
const HALF_LIFE_MS = 1000 * 60 * 60 * 24 * 10; // 10 days: older signals fade

function readSignals() {
  try {
    const raw = window.localStorage.getItem(SIGNALS_KEY);
    if (!raw) return { projects: {}, stages: {} };
    const parsed = JSON.parse(raw);
    return { projects: parsed.projects || {}, stages: parsed.stages || {} };
  } catch {
    return { projects: {}, stages: {} };
  }
}

function writeSignals(sig) {
  try { window.localStorage.setItem(SIGNALS_KEY, JSON.stringify(sig)); } catch { /* ignore */ }
}

// Record that the visitor engaged with a project page. Guards against
// rapid double-invocation (React StrictMode / remounts) so one visit
// counts once.
let _lastView = { slug: null, ts: 0 };
export function recordProjectView(slug) {
  if (!IMPROVEMENT_PROJECTS[slug]) return;
  const now = Date.now();
  if (_lastView.slug === slug && now - _lastView.ts < 1500) return;
  _lastView = { slug, ts: now };
  const sig = readSignals();
  const prev = sig.projects[slug] || { n: 0, ts: 0 };
  sig.projects[slug] = { n: prev.n + 1, ts: now };
  writeSignals(sig);
}

// Record a behavioural stage signal (usually the page-context stage).
export function recordStageSignal(stageKey) {
  if (!isImprovementStage(stageKey)) return;
  const sig = readSignals();
  sig.stages[stageKey] = (sig.stages[stageKey] || 0) + 1;
  writeSignals(sig);
}

// Recency-weighted score so a project viewed often AND recently wins.
function projectScore(entry) {
  if (!entry) return 0;
  const age = Date.now() - (entry.ts || 0);
  const recency = Math.max(0.15, Math.exp(-age / HALF_LIFE_MS));
  return (entry.n || 0) * recency;
}

/**
 * Resolve the visitor's current journey.
 * @returns {{ project: {slug,label}|null, stage: string|null,
 *             confidence: 'high'|'medium'|'low', projectViews: number,
 *             declared: string|null, inferred: string|null }}
 */
export function resolveJourney() {
  const sig = readSignals();

  // Declared stage = only counts if the visitor explicitly chose it.
  let declared = null;
  try {
    const flag = window.localStorage.getItem(JOURNEY_DECLARED_KEY) === '1';
    const s = window.localStorage.getItem(JOURNEY_STORAGE_KEY);
    if (flag && s && isImprovementStage(s)) declared = s;
  } catch { /* ignore */ }

  // Soft stored stage (may be a passive page-context seed).
  let storedSoft = null;
  try {
    const s = window.localStorage.getItem(JOURNEY_STORAGE_KEY);
    if (s && isImprovementStage(s)) storedSoft = s;
  } catch { /* ignore */ }

  // Inferred stage = strongest behavioural signal.
  let inferred = null;
  let best = 0;
  for (const [k, v] of Object.entries(sig.stages)) {
    if (isImprovementStage(k) && v > best) { best = v; inferred = k; }
  }

  // Resolved project: strongest by recency-weighted score, else the last
  // remembered project (kept for continuity with older sessions).
  let project = null;
  let bestScore = 0;
  for (const [slug, entry] of Object.entries(sig.projects)) {
    const score = projectScore(entry);
    if (IMPROVEMENT_PROJECTS[slug] && score > bestScore) { bestScore = score; project = slug; }
  }
  if (!project) {
    try {
      const raw = window.localStorage.getItem(JOURNEY_PROJECT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && IMPROVEMENT_PROJECTS[parsed.slug]) project = parsed.slug;
      }
    } catch { /* ignore */ }
  }

  const stage = declared || inferred || storedSoft || null;

  // Confidence: high when declared agrees with (or has no competing)
  // inference; medium when only one signal exists; low otherwise.
  let confidence = 'low';
  if (declared && (!inferred || inferred === declared)) confidence = 'high';
  else if (declared || inferred) confidence = 'medium';

  const projectViews = Object.values(sig.projects).reduce((a, e) => a + (e?.n || 0), 0);

  return {
    project: project ? { slug: project, label: IMPROVEMENT_PROJECTS[project].label } : null,
    stage,
    confidence,
    projectViews,
    declared,
    inferred,
  };
}
